import { Button, SearchableSelect } from 'components/core';
import { FunnelStage } from 'helpers/kpiBuilder/types';
import { OUTLINE_SMALL } from './buttonProps';
import { TextBox } from './TextBox';

export interface KpiOption {
  code: string;
  title: string;
}

interface FunnelStageRowProps {
  index: number;
  stage: FunnelStage;
  /** 'stage' funnels can also name who is still waiting after a step. */
  withGap: boolean;
  options: KpiOption[];
  /** KPIs that list who is still waiting after this step. */
  gapOptions: KpiOption[];
  /** The chosen step is not a plain-number KPI, so the funnel may come out empty. */
  notPlain: boolean;
  canRemove: boolean;
  onChange: (patch: Partial<FunnelStage>) => void;
  onRemove: () => void;
}

const textOf = (o: KpiOption): string => `${o.title} (${o.code})`;

interface PickProps {
  options: KpiOption[];
  value: string;
  placeholder: string;
  onPick: (code: string) => void;
}

const KpiPick = ({ options, value, placeholder, onPick }: PickProps) => {
  const current = options.find((o) => o.code === value);
  return (
    <SearchableSelect
      options={options.map(textOf)}
      selected={current ? textOf(current) : ''}
      placeholder={placeholder}
      onChange={(text) => onPick(options.find((o) => textOf(o) === text)?.code ?? '')}
    />
  );
};

// One step of a funnel: which existing KPI it shows, what to call it, and optionally who is left behind.
export const FunnelStageRow = ({ index, stage, withGap, options, gapOptions, notPlain, canRemove, onChange, onRemove }: FunnelStageRowProps) => {
  // The name follows the KPI while it is still the one filled in automatically; a name the admin typed is kept.
  const titleOf = (code: string): string => options.find((o) => o.code === code)?.title ?? '';
  const pick = (code: string) => {
    const autoNamed = !stage.label || stage.label === titleOf(stage.kpi);
    onChange({ kpi: code, label: autoNamed ? titleOf(code) : stage.label });
  };
  const remove = () => {
    onRemove();
  };

  return (
    <div className="border rounded p-3 mb-3 bg-white">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>Step {index + 1}</strong>
        {canRemove && (
          <Button {...OUTLINE_SMALL} onClick={remove}>
            Remove
          </Button>
        )}
      </div>
      <div className="row g-2">
        <div className="col-md-6">
          <KpiPick options={options} value={stage.kpi} placeholder="Choose a KPI for this step" onPick={pick} />
          {notPlain && (
            <div className="small text-warning-emphasis mt-1">
              This KPI lists people or needs its own handler instead of giving one plain number, so the funnel may show no data for this step.
            </div>
          )}
        </div>
        <div className="col-md-6">
          <TextBox placeholder="Name shown on the funnel" value={stage.label} onChange={(v) => onChange({ label: v })} />
        </div>
        {index > 0 && (
          <div className="col-12">
            <TextBox placeholder="Optional text after the percentage, e.g. “of Registered Enrolled”" value={stage.pill} onChange={(v) => onChange({ pill: v })} />
          </div>
        )}
        {withGap && (
          <>
            <div className="col-md-6">
              <KpiPick options={gapOptions} value={stage.gapKpi} placeholder="Optional: KPI listing who is still waiting" onPick={(code) => onChange({ gapKpi: code })} />
            </div>
            <div className="col-md-6">
              <TextBox placeholder="Name for the waiting group, e.g. Not Yet Enrolled" value={stage.gapLabel} onChange={(v) => onChange({ gapLabel: v })} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
